import React, { useState } from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import TrialSignupModal from '../components/TrialSignupModal';

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6 } }
};

const slideUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

interface PricingTier {
  name: string;
  price: string;
  period?: string;
  tagline: string;
  emotionalState: string;
  valueStatement: string;
  perfectFor: string;
  capabilities: string[];
  transformationPromise: string;
  cta: string;
  highlighted?: boolean;
  badge?: string;
  priceContext?: string;
}

const MarketingPricing = () => {
  const navigate = useNavigate();
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);

  const tiers: PricingTier[] = [
    {
      name: "Starter",
      price: "$15",
      period: "/month",
      tagline: "Never miss another call",
      emotionalState: "For those drowning in missed opportunities",
      valueStatement: "Finally answer every call without sacrificing your life",
      perfectFor: "Perfect for: The consultant missing leads during client meetings. The real estate agent losing listings during showings. The founder choosing between family dinner and investor calls.",
      capabilities: [
        "One double, always answering",
        "60 minutes of conversation monthly",
        "Your voice, your knowledge, your presence",
        "While you focus on high-value work"
      ],
      transformationPromise: "Transform from 'always missing something' to 'always present'",
      cta: "Start Being Everywhere",
      priceContext: "Less than a single hour of your time"
    },
    {
      name: "Professional",
      price: "$149",
      period: "/month",
      tagline: "Be in five conversations at once",
      emotionalState: "For those ready to scale without burning out",
      valueStatement: "Your expertise, multiplied. Your presence, amplified. Your time, reclaimed.",
      perfectFor: "Perfect for: The growing consultant juggling discovery calls. The sales professional managing multiple pipelines. The founder wearing every hat simultaneously.",
      capabilities: [
        "Five doubles working in parallel",
        "1,000 minutes of conversation monthly",
        "Each with unique personality settings",
        "Handle every call while you handle strategy"
      ],
      transformationPromise: "Transform from 'stretched thin' to 'strategically everywhere'",
      cta: "Go Professional Now",
      highlighted: true,
      badge: "Most Popular",
      priceContext: "ROI in your first saved deal"
    },
    {
      name: "Business",
      price: "$799",
      period: "/month",
      tagline: "Unlimited you, everywhere",
      emotionalState: "For those building empires while having lives",
      valueStatement: "True omnipresence. Every call answered. Every opportunity captured. Every moment yours to choose.",
      perfectFor: "Perfect for: The agency owner scaling without hiring. The sales team crushing quotas without crushing spirits. The business that never sleeps but lets you rest.",
      capabilities: [
        "Unlimited doubles, infinite reach",
        "5,000 minutes monthly",
        "Team collaboration features",
        "API access for full integration",
        "Your business runs while you live"
      ],
      transformationPromise: "Transform from 'business owner' to 'empire builder'",
      cta: "Build Your Empire",
      priceContext: "Cheaper than one employee. Better than ten."
    },
    {
      name: "Enterprise",
      price: "$2,999",
      period: "/month",
      tagline: "Run your double dynasty",
      emotionalState: "For those redefining what's possible",
      valueStatement: "When unlimited isn't enough. When everywhere is just the beginning. When your ambition needs infrastructure.",
      perfectFor: "Perfect for: The organization replacing call centers. The enterprise scaling globally. The visionary building the future of work.",
      capabilities: [
        "Unlimited everything",
        "25,000 minutes monthly",
        "White-label options",
        "Dedicated success team",
        "Custom AI training",
        "SLA guarantees"
      ],
      transformationPromise: "Transform from 'industry leader' to 'industry definer'",
      cta: "Let's Talk Scale",
      priceContext: "The price of transformation"
    }
  ];

  const faqs = [
    {
      question: "What happens if I exceed my minutes?",
      answer: "Your doubles keep working. Additional minutes are just $0.15 each. No interruption. No drama. We'll nudge you when it makes sense to upgrade."
    },
    {
      question: "Can I change plans anytime?",
      answer: "Upgrade instantly. Downgrade at month end. No contracts. No guilt. No questions."
    },
    {
      question: "How quickly can I set up my first double?",
      answer: "Ten minutes from signup to first call. Voice cloning takes 30 seconds. Knowledge upload is instant. Most users receive their first real call within 24 hours."
    },
    {
      question: "What if it doesn't sound like me?",
      answer: "It will. But if you're not convinced in 30 days, full refund. No forms. No retention team. Just your money back."
    },
    {
      question: "Is my data secure?",
      answer: "Bank-level encryption. Your voice is yours alone. We never share, sell, or listen. Privacy isn't a feature—it's the foundation."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Trial Signup Modal */}
      <TrialSignupModal isOpen={showOnboardingModal} onClose={() => setShowOnboardingModal(false)} />

      {/* Header */}
      <header className="fixed w-full bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button onClick={() => navigate('/')} className="text-2xl font-bold text-gray-900">
              DoubleMe
            </button>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="text-gray-600 hover:text-gray-900 font-medium transition"
              >
                Home
              </button>
              <button
                onClick={() => setShowOnboardingModal(true)}
                className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg font-medium transition"
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
          >
            <motion.h1 variants={slideUp} className="text-5xl md:text-6xl font-bold text-gray-900 mb-4">
              Choose your multiplication factor
            </motion.h1>
            <motion.p variants={slideUp} className="text-xl text-gray-600">
              How many places do you need to be?
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {tiers.map((tier, index) => (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-white rounded-2xl border-2 p-6 ${
                  tier.highlighted
                    ? 'border-primary shadow-2xl scale-105'
                    : 'border-gray-200 hover:border-dark'
                } transition-all`}
              >
                {tier.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-primary text-white px-4 py-1 rounded-full text-sm font-semibold">
                      {tier.badge}
                    </span>
                  </div>
                )}

                {/* Tier Header */}
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-4xl font-bold text-gray-900">{tier.price}</span>
                    {tier.period && <span className="text-gray-600">{tier.period}</span>}
                  </div>
                  {tier.priceContext && (
                    <p className="text-sm text-gray-600 italic">{tier.priceContext}</p>
                  )}
                  <p className="text-lg font-semibold text-primary mt-3">{tier.tagline}</p>
                </div>

                {/* Emotional State */}
                <div className="mb-4">
                  <p className="text-sm text-gray-600 italic">{tier.emotionalState}</p>
                </div>

                {/* Value Statement */}
                <div className="mb-4">
                  <p className="text-sm text-gray-700">{tier.valueStatement}</p>
                </div>

                {/* Perfect For */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700">{tier.perfectFor}</p>
                </div>

                {/* Capabilities */}
                <div className="mb-6 space-y-3">
                  <h4 className="font-semibold text-gray-900">What becomes possible:</h4>
                  {tier.capabilities.map((capability, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{capability}</span>
                    </div>
                  ))}
                </div>

                {/* Transformation Promise */}
                <div className="mb-6 p-4 bg-primary/5 rounded-lg">
                  <p className="text-sm font-medium text-dark">{tier.transformationPromise}</p>
                </div>

                {/* CTA */}
                <button
                  onClick={() => setShowOnboardingModal(true)}
                  className={`w-full py-3 rounded-lg font-semibold transition ${
                    tier.highlighted
                      ? 'bg-primary hover:bg-primary/90 text-white'
                      : 'bg-white hover:bg-gray-50 text-primary border-2 border-purple-600'
                  }`}
                >
                  {tier.cta}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Calculator */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center"
          >
            <motion.h2 variants={slideUp} className="text-4xl font-bold text-gray-900 mb-4">
              Let's do the uncomfortable math
            </motion.h2>

            <motion.div variants={slideUp} className="bg-white rounded-2xl border-2 border-gray-200 p-8 mt-8">
              <div className="space-y-6">
                <div>
                  <label className="block text-left text-gray-700 font-medium mb-2">
                    What's your time worth per hour?
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-gray-900">$</span>
                    <input
                      type="number"
                      defaultValue={100}
                      className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg text-2xl font-bold text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-6 py-6 border-y border-gray-200">
                  <div>
                    <div className="text-sm text-gray-600 mb-1">One missed call</div>
                    <div className="text-2xl font-bold text-red-600">-$5,000</div>
                    <div className="text-xs text-gray-500">Lost opportunity average</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">DoubleMe monthly cost</div>
                    <div className="text-2xl font-bold text-primary">0.15 hours</div>
                    <div className="text-xs text-gray-500">Of your time value</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">ROI</div>
                    <div className="text-2xl font-bold text-green-600">∞</div>
                    <div className="text-xs text-gray-500">Embarrassingly high</div>
                  </div>
                </div>

                <p className="text-lg text-gray-700 font-medium">
                  The question isn't whether you can afford DoubleMe. It's whether you can afford not to have it.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 variants={slideUp} className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Common questions
            </motion.h2>

            <motion.div variants={stagger} className="space-y-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  variants={slideUp}
                  className="bg-white border border-gray-200 rounded-xl p-6"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.question}</h3>
                  <p className="text-gray-700">{faq.answer}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Money-Back Guarantee */}
      <section className="py-20 bg-primary/5">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 variants={slideUp} className="text-3xl font-bold text-gray-900 mb-4">
              30 days to fall in love or your money back
            </motion.h2>
            <motion.p variants={slideUp} className="text-lg text-gray-700 max-w-2xl mx-auto">
              We're so confident you'll wonder how you lived without doubles, we're eliminating the risk entirely. Try it for 30 days. If your double doesn't transform how you work, we'll refund every penny and part as friends.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 variants={slideUp} className="text-4xl font-bold mb-4">
              Ready to be everywhere?
            </motion.h2>
            <motion.p variants={slideUp} className="text-xl text-gray-300 mb-8">
              Start your 14-day free trial. No credit card required.
            </motion.p>
            <motion.button
              variants={slideUp}
              onClick={() => setShowOnboardingModal(true)}
              className="bg-white hover:bg-gray-100 text-gray-900 px-8 py-4 rounded-full font-semibold text-lg transition flex items-center gap-2 mx-auto"
            >
              Create Your First Double
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Onboarding Modal */}
      {showOnboardingModal && (
        <OnboardingModal onClose={() => setShowOnboardingModal(false)} />
      )}
    </div>
  );
};

export default MarketingPricing;
